import { fireErrorAnalytics } from '@jira/platform__error-reporting/src/fireErrorAnalytics.tsx';

import type {
	Collection,
	ViewResponse,
} from '@jira/polaris__domain-collection/src/SrcViewResponse.tsx';
import type { DocNode as ADF } from '@atlaskit/adf-schema/doc';
import fetchJson from '@atlassian/jira-fetch/src/utils/as-json.tsx';
import {
	performDeleteRequest,
	performPostRequest,
	performPutRequest,
} from '@atlassian/jira-fetch/src/utils/requests.tsx';
import { fg } from '@atlassian/jira-feature-gating';
import { getGravityServicePathPrefix } from '@jira/polaris__lib-jpd-gravity/src/getGravityServicePathPrefix.tsx';
import { getJpdViewsServicePathPrefix } from '@jira/polaris__lib-jpd-views-service/src/index.tsx';
import { createErrorAnalytics } from '@jira/polaris__lib-errors/src/createErrorAnalytics.tsx';
import { extractProjectKeysFromJql, extractFiltersJqlFromJql } from './getJQLForCollection.tsx';
import { transformView } from './transformView.tsx';

/**
 * Collections are being migrated from the JPD Views Service to Gravity. Gravity
 * mirrors the VS wire contract, so only the base path changes behind the flag.
 */
const getCollectionsServicePathPrefix = (): string =>
	fg('jpd_collections_gravity_migration_fe')
		? getGravityServicePathPrefix()
		: getJpdViewsServicePathPrefix();

type CollectionCreateRequestBody = {
	name: string;
	emoji?: string;
	color?: string;
	jql: string;
};

type CollectionUpdateRequestBody = CollectionCreateRequestBody & {
	description?: ADF;
};

type CollectionResponse = {
	uuid: string;
	name: string;
	emoji?: string;
	color?: string;
	jql: string;
	owner: string;
};

type CollectionFullResponse = CollectionResponse & {
	views?: ViewResponse[];
};

const generateProjectKeys = (jql: string) => {
	return extractProjectKeysFromJql(jql);
};

export const createCollection = (
	collectionToCreate: CollectionCreateRequestBody,
): Promise<Collection> =>
	performPostRequest<CollectionResponse>(`${getCollectionsServicePathPrefix()}/v1/collections`, {
		body: JSON.stringify(collectionToCreate),
	}).then((response) => ({
		uuid: response.uuid,
		name: response.name,
		emoji: response.emoji,
		color: response.color,
		jql: response.jql,
		ownerId: response.owner,
		projectKeys: generateProjectKeys(response.jql),
		filtersJql: extractFiltersJqlFromJql(response.jql),
	}));

export const fetchCollection = (
	collectionUUID: string,
	shouldIncludeViews = false,
): Promise<Collection> =>
	fetchJson<CollectionFullResponse>(
		`${getCollectionsServicePathPrefix()}/v1/collections/${collectionUUID}${shouldIncludeViews ? '?expand=views' : ''}`,
	).then((response) => ({
		uuid: response.uuid,
		name: response.name,
		emoji: response.emoji,
		color: response.color,
		jql: response.jql,
		ownerId: response.owner,
		views: response.views?.map(transformView),
		projectKeys: generateProjectKeys(response.jql),
		filtersJql: extractFiltersJqlFromJql(response.jql),
	}));

export const fetchAllCollections = (): Promise<Collection[]> =>
	fetchJson<{ collections: CollectionResponse[] }>(
		`${getCollectionsServicePathPrefix()}/v1/collections`,
	).then(({ collections }) =>
		collections.map((collection) => ({
			uuid: collection.uuid,
			name: collection.name,
			emoji: collection.emoji,
			color: collection.color,
			jql: collection.jql,
			ownerId: collection.owner,
			projectKeys: generateProjectKeys(collection.jql),
			filtersJql: extractFiltersJqlFromJql(collection.jql),
		})),
	);

export const deleteCollection = async (collectionUUID: string): Promise<void> => {
	await performDeleteRequest(
		`${getCollectionsServicePathPrefix()}/v1/collections/${collectionUUID}`,
	);
};

export const updateCollection = async (
	collectionUUID: string,
	collectionToUpdate: CollectionUpdateRequestBody,
): Promise<void> => {
	await performPutRequest(`${getCollectionsServicePathPrefix()}/v1/collections/${collectionUUID}`, {
		body: JSON.stringify(collectionToUpdate),
	});
};

export const fetchCollectionsViewsCounts = (
	collectionsUUIDs: string[],
): Promise<Record<string, number | undefined>> =>
	performPostRequest(`${getCollectionsServicePathPrefix()}/v1/collections/views-count`, {
		body: JSON.stringify({ uuids: collectionsUUIDs }),
	});

export const fetchCollectionDescription = (collectionUUID: string): Promise<ADF | null> =>
	fetchJson(`${getCollectionsServicePathPrefix()}/v1/collections/${collectionUUID}/description`);

export const cloneCollection = (
	collectionUUID: string,
	newCollectionName: string,
): Promise<{
	uuid: string;
}> =>
	performPostRequest<{ uuid: string }>(
		`${getCollectionsServicePathPrefix()}/v1/collections/${collectionUUID}/clone`,
		{
			body: JSON.stringify({ name: newCollectionName }),
		},
	);

export const getIdeasCountForCollection = (jql: string): Promise<number> =>
	performPostRequest('/rest/polaris/issues/searchCountForJql', {
		body: JSON.stringify({
			jql,
			archived: 'ACTIVE_ONLY',
		}),
	})
		.then((response) => response.count)
		.catch((error) => {
			fireErrorAnalytics(
				createErrorAnalytics('polaris.error.getIdeasCountForCollectionFailed', error),
			);
			throw error;
		});
